import React, { useState, useMemo, useRef, useCallback } from 'react'

import type { FormikProps } from 'formik'

import {
  Stack,
  Grid,
  GridItem,
  Typography,
  Box,
  HStack,
  Form,
  Select,
  Skeleton,
  EmptyState,
} from '@pnld/components-web'

import {
  useDashboardCitiesByState,
  useDashboardCensusEducationStages,
} from '@/api/dashboard/queries'
import { PieChart, BarChart } from '@/components/Charts'
import SlidesDisplay from '@/components/Display/Slides'
import TotalizerDisplay from '@/components/Display/Totalizer'
import { stateOptions, stateToRegionMap } from '@/data/dashboard'
import { State } from '@/types/dashboard'

import { CensusSingleYearProps } from './types'

interface FilterFormValues {
  states: State[]
  cities: number[]
}

export const CensusSingleYear: React.FC<CensusSingleYearProps> = ({
  census,
  globalFilters,
}) => {
  const globalStates = globalFilters?.states || []
  const globalCities = globalFilters?.cities || []

  const initialValues: FilterFormValues = {
    states: globalStates.length > 0 ? globalStates : [],
    cities: globalCities.length > 0 ? globalCities : [],
  }

  const [formValues, setFormValues] = useState<FilterFormValues>(initialValues)
  const formRef = useRef<FormikProps<FilterFormValues> | null>(null)
  const selectRefs = useRef<Record<string, any>>({})

  const mapToBaseFilters = useCallback(
    (localStates: State[], localCities: number[]) => {
      return {
        coRegion: globalFilters?.region || undefined,
        coUf:
          localStates.length > 0
            ? localStates
            : globalFilters?.states?.length
            ? globalFilters.states
            : undefined,
        coCity:
          localCities.length > 0
            ? localCities
            : globalFilters?.cities?.length
            ? globalFilters.cities
            : undefined,
        coEntity: globalFilters?.schools?.length
          ? globalFilters.schools
          : undefined,
        tpDependency: globalFilters?.dependencies?.length
          ? globalFilters.dependencies
          : undefined,
        tpLocation: globalFilters?.locations?.length
          ? globalFilters.locations
          : undefined,
        coSphere: globalFilters?.spheres?.length
          ? globalFilters.spheres
          : undefined,
        stPnldService: globalFilters?.hasPndlService || undefined,
        coEducationStage: globalFilters?.educationStages?.length
          ? globalFilters.educationStages
          : undefined,
        coYearGrade: globalFilters?.yearGrades?.length
          ? globalFilters.yearGrades
          : undefined,
      }
    },
    [globalFilters]
  )

  const { data: cities, isLoading: isCitiesLoadingRaw } =
    useDashboardCitiesByState(
      formValues.states.length > 0 ? { coUf: formValues.states } : { coUf: [] },
      { enabled: formValues.states.length > 0 }
    )

  const isCitiesLoading = isCitiesLoadingRaw && formValues.states.length > 0

  const {
    data: censusEducationStages,
    isLoading: isCensusEducationStagesLoading,
  } = useDashboardCensusEducationStages(
    {
      nuCensusYear: [census.nuCensusYear],
      ...mapToBaseFilters(formValues.states, formValues.cities),
    },
    { enabled: !!census }
  )

  const handleOnSubmit = (values: FilterFormValues) => {
    setFormValues(values)
  }

  const handleStatesChange = useCallback((options: any) => {
    const states = options ? options.map((o: any) => o.value) : []
    setFormValues(prev => ({ ...prev, states, cities: [] }))
    formRef.current?.setFieldValue('states', states)
    formRef.current?.setFieldValue('cities', [])
    selectRefs.current.cities?.clearValue?.()
  }, [])

  const handleCitiesChange = useCallback((options: any) => {
    const cities = options ? options.map((o: any) => o.value) : []
    setFormValues(prev => ({ ...prev, cities }))
    formRef.current?.setFieldValue('cities', cities)
  }, [])

  const availableStateOptions = useMemo(() => {
    if (globalStates.length > 0) {
      return stateOptions.filter(option => globalStates.includes(option.value))
    }

    if (globalFilters?.region) {
      return stateOptions.filter(
        option => stateToRegionMap[option.value] === globalFilters.region
      )
    }

    return stateOptions
  }, [globalStates, globalFilters?.region])

  const availableCityOptions = useMemo(() => {
    if (formValues.states.length > 0) {
      return (
        cities?.map(city => ({
          label: city.label,
          value: city.value,
        })) || []
      )
    }
    return []
  }, [formValues.states, cities])

  const isStateDisabled = globalStates.length > 0
  const isCityDisabled =
    globalCities.length > 0 || formValues.states.length === 0

  return (
    <>
      <Stack gap={6}>
        <Grid templateColumns="repeat(3, 1fr)" gap={6}>
          <GridItem>
            <TotalizerDisplay
              number={census.students}
              label="Total de alunos Censo"
              variant="highlight"
              icon="user-graduate"
            />
          </GridItem>
          <GridItem>
            <TotalizerDisplay
              number={census.classes}
              label="Total de turmas presentes no Censo base"
              icon="users"
            />
          </GridItem>
          <GridItem>
            <TotalizerDisplay
              number={census.schools}
              label="Total de escolas presentes no Censo base"
              icon="school"
            />
          </GridItem>
        </Grid>

        <Grid templateColumns="repeat(2, 1fr)" gap={3}>
          <GridItem>
            <PieChart
              data={census.region.map(r => ({
                name: r.label,
                value: r.value,
              }))}
              centralLabel={
                <Stack spacing={0}>
                  <Typography variant="h-2xs">
                    Alunos por
                    <br />
                    Região
                  </Typography>
                  <Typography color="brand.highlight.dark" variant="h-2xs">
                    {census.nuCensusYear}
                  </Typography>
                </Stack>
              }
              legendDirection="column"
              chartHeight={320}
              rightGutter={250}
              outerRadius={85}
              innerRadius={60}
            />
          </GridItem>
          <GridItem>
            <PieChart
              data={census.dependency.map(d => ({
                name: d.label,
                value: d.value,
              }))}
              centralLabel={
                <Stack spacing={0}>
                  <Typography variant="h-2xs">
                    Alunos por
                    <br />
                    Dependência
                  </Typography>
                  <Typography color="brand.highlight.dark" variant="h-2xs">
                    {census.nuCensusYear}
                  </Typography>
                </Stack>
              }
              legendDirection="column"
              chartHeight={320}
              rightGutter={250}
              outerRadius={85}
              innerRadius={60}
            />
          </GridItem>
        </Grid>

        <Grid templateColumns="repeat(2, 1fr)" gap={3}>
          <GridItem>
            <PieChart
              data={census.location.map(l => ({
                name: l.label,
                value: l.value,
              }))}
              centralLabel={
                <Stack spacing={0}>
                  <Typography variant="h-2xs">
                    Alunos por
                    <br />
                    Localização
                  </Typography>
                  <Typography color="brand.highlight.dark" variant="h-2xs">
                    {census.nuCensusYear}
                  </Typography>
                </Stack>
              }
              legendDirection="column"
              chartHeight={320}
              rightGutter={250}
              outerRadius={85}
              innerRadius={60}
            />
          </GridItem>
          <GridItem>
            <PieChart
              data={census.educationStage.map(e => ({
                name: e.label,
                value: e.value,
              }))}
              centralLabel={
                <Stack spacing={0}>
                  <Typography variant="h-2xs">
                    Alunos por
                    <br />
                    Etapa de ensino
                  </Typography>
                  <Typography color="brand.highlight.dark" variant="h-2xs">
                    {census.nuCensusYear}
                  </Typography>
                </Stack>
              }
              legendDirection="column"
              chartHeight={320}
              rightGutter={250}
              outerRadius={85}
              innerRadius={60}
            />
          </GridItem>
        </Grid>
      </Stack>

      <Stack
        padding={4}
        gap={4}
        borderRadius="md"
        border="2px solid"
        borderColor="gray.200"
      >
        <Form
          innerRef={formRef}
          initialValues={initialValues}
          onSubmit={handleOnSubmit}
          enableReinitialize={true}
        >
          <HStack>
            <Select
              selectRef={ref => (selectRefs.current.states = ref)}
              name="states"
              label="Filtrar por estado"
              placeholder="Todos"
              options={availableStateOptions}
              disabled={isStateDisabled}
              isMulti
              {...({ onChange: handleStatesChange } as any)}
            />
            <Select
              selectRef={ref => (selectRefs.current.cities = ref)}
              name="cities"
              label="Filtrar por Munícipio"
              placeholder="Todos"
              options={availableCityOptions}
              disabled={isCityDisabled || isCitiesLoading}
              isMulti
              {...(isCitiesLoading && { isLoading: true })}
              {...({ onChange: handleCitiesChange } as any)}
            />
          </HStack>
        </Form>

        {isCensusEducationStagesLoading ? (
          <Skeleton height={360} borderRadius="md" />
        ) : censusEducationStages &&
          censusEducationStages.length > 0 &&
          censusEducationStages[0].educationData.length > 0 ? (
          <SlidesDisplay
            items={censusEducationStages[0].educationData.map(stage => ({
              title: stage.dsEducationStage,
              content: (
                <Box height={300}>
                  <BarChart
                    data={stage.yearGrades.map(grade => ({
                      name: grade.label,
                      value: grade.value,
                    }))}
                  />
                </Box>
              ),
            }))}
          />
        ) : (
          <EmptyState
            title="Nenhum dado encontrado"
            description="Não há dados de etapas de ensino para os filtros selecionados."
            iconProps={{ name: 'table' }}
            shaped="false"
          />
        )}
      </Stack>
    </>
  )
}
